import { useState } from "react";
import { FileSpreadsheet, ChevronDown, Loader2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { downloadArtifactTables } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { hasLivePricing, tableCount, workingView } from "@/lib/tables";
import { toast } from "sonner";

export default function TablesBar({ artifact, working = false }) {
  const view = workingView(artifact, working);
  const [busy, setBusy] = useState(null);
  if (!view) return null;
  const n = tableCount(view.content);
  const live = hasLivePricing(view);
  if (n === 0 && !live) return null;

  const nLabel = n === 1 ? "1 tabela" : `${n} tabelas`;
  const copy = n > 0
    ? `Este relatório tem ${nLabel}. Dá para baixar só elas.`
    : "A planilha desta precificação pode ser baixada com as contas.";
  const pending = Boolean(working && artifact?.revision?.content);

  const run = async (kind, mode) => {
    if (!view.id || busy) return;
    setBusy(`${kind}-${mode}`);
    try {
      await downloadArtifactTables(view.id, { kind, mode, working });
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-4 lg:px-6 py-2.5 border-b border-brand-line bg-brand-sage/10"
      data-testid="tables-bar"
    >
      <p className="flex-1 min-w-0 text-[12.5px] leading-snug text-brand-ink2 font-ui">
        <span className="text-brand-sage font-ui tracking-wide uppercase text-[10.5px] mr-2">Tabelas</span>
        {copy}
        {pending && (
          <span className="block sm:inline sm:ml-1 text-brand-rose">Ajuste ainda não salvo na Biblioteca.</span>
        )}
      </p>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="tables-download"
            disabled={Boolean(busy)}
            className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-elevated border border-brand-line text-[12.5px] font-ui text-brand-ink hover:border-brand-rose hover:text-brand-rose transition-colors min-h-[44px] shrink-0 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
            Baixar tabelas
            <ChevronDown className="w-3.5 h-3.5 opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72 bg-brand-surface border-brand-line">
          {live && (
            <DropdownMenuItem
              data-testid="tables-xlsx-live"
              className="cursor-pointer text-[13px] text-brand-ink focus:bg-brand-elevated focus:text-brand-ink"
              onSelect={() => run("xlsx", "live")}
            >
              Planilha com contas (1 tabela por aba)
            </DropdownMenuItem>
          )}
          {n > 0 && (
            <DropdownMenuItem
              data-testid="tables-xlsx-simple"
              className="cursor-pointer text-[13px] text-brand-ink focus:bg-brand-elevated focus:text-brand-ink"
              onSelect={() => run("xlsx", "tables")}
            >
              {live ? "Planilha simples (1 tabela por aba)" : "Planilha (1 tabela por aba)"}
            </DropdownMenuItem>
          )}
          {n > 0 && (
            <DropdownMenuItem
              data-testid="tables-csv"
              className="cursor-pointer text-[13px] text-brand-ink focus:bg-brand-elevated focus:text-brand-ink"
              onSelect={() => run("csv", "tables")}
            >
              CSV (1 tabela por arquivo)
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
